import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CardType } from '../../../enums/card-type';
import { QuestionnaireModel } from '../../../api/models/questionnaire-model';
import { QuestionnaireQuestionSetSummaryModel } from '../../../api/models/questionnaire-question-set-summary-model';

/*
  Component representing a questionnaire card / tile, containing info such as the image, title, description, and
  created / modified dates
 */
@Component({
  selector: 'app-questionnaire-card',
  templateUrl: './questionnaire-card.component.html',
  standalone: false,
})
export class QuestionnaireCardComponent {
  /*
    The questionnaire being built. If no input is passed, a new questionnaire will instead be created
  */
  @Input({ required: true }) questionnaire?: QuestionnaireModel | null;

  /*
    The card type, which determines whether the grid or list view is presented
   */
  @Input({ required: true }) cardType!: CardType;

  /*
    The text on the button
  */
  @Input({ required: false }) buttonText!: string;

  @Input({ required: false }) showArchiveAction = false;

  /*
    Event emitted when button on the card is clicked
   */
  @Output() buttonClickedEvent = new EventEmitter<QuestionnaireModel>();

  @Output() archiveClickedEvent = new EventEmitter<QuestionnaireModel>();

  protected readonly CardType = CardType;
  protected readonly visibleQuestionSetChipLimit = 3;

  archiveQuestionnaire(
    event: MouseEvent,
    questionnaire: QuestionnaireModel,
  ): void {
    event.preventDefault();
    event.stopPropagation();
    this.archiveClickedEvent.emit(questionnaire);
  }

  protected getQuestionCount(questionnaire: QuestionnaireModel): number {
    if (
      Array.isArray(questionnaire.questions) &&
      questionnaire.questions.length > 0
    ) {
      return questionnaire.questions.length;
    }

    return this.getQuestionSets(questionnaire).reduce(
      (total, questionSet) => total + (questionSet.questions?.length ?? 0),
      0,
    );
  }

  protected getQuestionSetCount(questionnaire: QuestionnaireModel): number {
    return this.getQuestionSets(questionnaire).length;
  }

  protected getVisibleQuestionSetChips(
    questionnaire: QuestionnaireModel,
  ): string[] {
    return this.getQuestionSetNames(questionnaire).slice(
      0,
      this.visibleQuestionSetChipLimit,
    );
  }

  protected getHiddenQuestionSetCount(
    questionnaire: QuestionnaireModel,
  ): number {
    return Math.max(
      this.getQuestionSetNames(questionnaire).length -
        this.visibleQuestionSetChipLimit,
      0,
    );
  }

  private getQuestionSetNames(questionnaire: QuestionnaireModel): string[] {
    return this.getQuestionSets(questionnaire)
      .map((questionSet) => questionSet.name?.trim())
      .filter((name): name is string => !!name);
  }

  private getQuestionSets(
    questionnaire: QuestionnaireModel,
  ): QuestionnaireQuestionSetSummaryModel[] {
    const questionSets = questionnaire.questionSets;

    if (!Array.isArray(questionSets)) {
      return [];
    }

    return [...questionSets].sort(
      (left, right) =>
        (left.index ?? Number.MAX_SAFE_INTEGER) -
        (right.index ?? Number.MAX_SAFE_INTEGER),
    );
  }
}
